import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement

// Základní WYSIWYG editor postavený na nativním contenteditable + document.execCommand (bez
// externí editor knihovny). Výstup je HTML, sanitizované při renderu přes RichContent. Zjednodušená
// varianta editoru z bowlingligy (nadpis/odstavec/tučně/kurzíva/seznamy/odkaz).
private fun exec(cmd: String, value: String? = null) {
    document.asDynamic().execCommand(cmd, false, value)
}

@Composable
private fun ToolbarButton(label: String, title: String, onAction: () -> Unit) {
    Button(attrs = {
        classes("editor-tool")
        attr("type", "button")
        attr("title", title)
        onMouseDown { it.preventDefault() } // zachová výběr/kurzor v editoru při kliku na tlačítko
        onClick { onAction() }
    }) { Text(label) }
}

/**
 * Editor emituje aktuální HTML přes [onHtmlChange] při každé změně. Počáteční obsah se nastaví jednou
 * a dál ho spravuje prohlížeč (Compose contenteditable nikdy nepřepisuje -> kurzor neskáče).
 */
@Composable
fun ContentEditor(initialHtml: String, onHtmlChange: (String) -> Unit) {
    var editorEl by remember { mutableStateOf<HTMLElement?>(null) }

    fun syncHtml() { editorEl?.let { onHtmlChange(it.innerHTML) } }

    // execCommand nespolehlivě spouští input event -> po formátovacím příkazu HTML vytlačíme ručně.
    fun cmd(command: String, value: String? = null) { exec(command, value); syncHtml() }

    Div(attrs = { classes("editor-toolbar") }) {
        ToolbarButton("H3", "Nadpis") { cmd("formatBlock", "h3") }
        ToolbarButton("¶", "Odstavec") { cmd("formatBlock", "p") }
        ToolbarButton("B", "Tučně") { cmd("bold") }
        ToolbarButton("I", "Kurzíva") { cmd("italic") }
        ToolbarButton("•", "Odrážkový seznam") { cmd("insertUnorderedList") }
        ToolbarButton("1.", "Číslovaný seznam") { cmd("insertOrderedList") }
        ToolbarButton("🔗", "Odkaz") {
            val url = window.prompt("URL odkazu:")
            if (!url.isNullOrBlank()) cmd("createLink", url)
        }
    }

    Div(attrs = {
        classes("editor-area")
        attr("contenteditable", "true")
    }) {
        DisposableRefEffect(Unit) { el ->
            val html = el as HTMLElement
            html.innerHTML = initialHtml
            editorEl = html
            val inputListener: (org.w3c.dom.events.Event) -> Unit = { syncHtml() }
            val pasteListener: (org.w3c.dom.events.Event) -> Unit = { ev ->
                // Vloženému HTML (např. z Wordu) srazíme formátování na čistý text přes allowlist.
                val clipboard = ev.asDynamic().clipboardData
                val pastedHtml = clipboard?.getData("text/html") as? String
                if (!pastedHtml.isNullOrBlank()) {
                    ev.preventDefault()
                    exec("insertHTML", sanitizeHtml(pastedHtml))
                    syncHtml()
                }
            }
            html.addEventListener("input", inputListener)
            html.addEventListener("paste", pasteListener)
            onDispose {
                html.removeEventListener("input", inputListener)
                html.removeEventListener("paste", pasteListener)
                editorEl = null
            }
        }
    }
}
